"use client";
import React, { useState, useEffect, useRef } from "react";
import {
  Megaphone,
  Users,
  Workflow,
  BarChart,
  Target as TargetIcon,
  Handshake,
  MapPin,
  Share2,
  ArrowRight,
  Award,
  CheckCircle,
  TrendingUp,
} from "lucide-react";
import Footer from "../components/Footer";
import Header from "../components/Header";
import PopupForm from "../components/PopupForm"; // ✅ correct import

const useInView = (threshold = 0.1) => {
  const [isInView, setIsInView] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setIsInView(true);
    }, { threshold });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, isInView];
};

const AnimatedCounter = ({ end, duration = 2000, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  useEffect(() => {
    if (!started) return;
    let startTime;
    const animate = (time) => {
      if (!startTime) startTime = time;
      const progress = Math.min((time - startTime) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(ease * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [end, duration, started]);
  return <span onMouseEnter={() => setStarted(true)}>{count}{suffix}</span>;
};

const coreOfferings = [
  {
    title: "End-to-End Marketing Solutions",
    icon: <Megaphone className="w-12 h-12 text-[#741112]" />,
    stats: "95% Success Rate",
    description:
      "Comprehensive marketing strategies that drive measurable results across all channels",
    highlights: ["Digital Marketing", "Traditional Advertising", "Brand Development"],
  },
  {
    title: "Experienced Sales Force",
    icon: <Users className="w-12 h-12 text-[#741112]" />,
    stats: "70+ Experts",
    description:
      "Seasoned professionals with deep market knowledge and proven track records",
    highlights: ["Expert Consultants", "Local Market Knowledge", "Proven Success"],
  },
  {
    title: "End-to-End Sales Process",
    icon: <Workflow className="w-12 h-12 text-[#741112]" />,
    stats: "100% Process Driven",
    description:
      "Streamlined processes designed for efficiency and maximum conversion rates",
    highlights: ["Complete Sales Process", "Systematic Approach", "Result Oriented"],
  },
];

const marketingSolutions = [
  {
    text: "Market Assessment & Analysis",
    icon: <BarChart className="w-8 h-8 text-[#741112]" />,
    description:
      "Deep dive into market trends, competitor analysis, and opportunity identification",
    benefit: "Make data-driven decisions",
  },
  {
    text: "Effective Sales Planning & Strategy",
    icon: <TargetIcon className="w-8 h-8 text-[#741112]" />,
    description:
      "Strategic roadmaps tailored to your specific market and business objectives",
    benefit: "Maximize ROI potential",
  },
  {
    text: "Advertisement & Public Communication",
    icon: <Megaphone className="w-8 h-8 text-[#741112]" />,
    description:
      "Multi-channel campaigns that resonate with your target audience",
    benefit: "Boost brand visibility",
  },
  {
    text: "Client Sourcing & Closing",
    icon: <Handshake className="w-8 h-8 text-[#741112]" />,
    description:
      "End-to-end client acquisition from lead generation to deal closure",
    benefit: "Increase conversion rates",
  },
  {
    text: "Onsite Mobilization",
    icon: <MapPin className="w-8 h-8 text-[#741112]" />,
    description:
      "Strategic setup and deployment of resources at optimal locations",
    benefit: "Enhance market presence",
  },
  {
    text: "Channel Partner Activation",
    icon: <Share2 className="w-8 h-8 text-[#741112]" />,
    description:
      "Building and nurturing strategic partnerships for expanded reach",
    benefit: "Scale business growth",
  },
];

const ServicesPage = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [coreRef] = useInView(0.2);
  const [solutionsRef] = useInView(0.2);
  const [showPopup, setShowPopup] = useState(false); // ✅ local popup state

  return (
    <>
      <Header />
      <main className="bg-white overflow-hidden">
        {/* Core Offerings */}
        <section ref={coreRef} className="bg-white py-24 px-4 relative">
          <div className="text-center mb-20">
            <div className="inline-block relative mb-6">
              <div className="bg-[#741112] p-4 rounded-full shadow-lg">
                <Award className="w-8 h-8 text-white" />
              </div>
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-[#741112] mb-6">
              Our Core Offerings
            </h2>
            <div className="w-32 h-1 bg-[#741112] mx-auto rounded-full mb-4" />
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              We empower stakeholders across the real estate ecosystem through innovative solutions, collaborative partnerships, and deep expertise
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {coreOfferings.map((item, idx) => (
              <div
                key={idx}
                className="group relative bg-white shadow-lg border border-gray-100 rounded-2xl p-8 hover:shadow-2xl transition-all duration-500 overflow-hidden"
                style={{ transitionDelay: `${idx * 0.2}s` }}
                onMouseEnter={() => setHoveredCard(idx)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <div className="absolute inset-0 bg-[#741112]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative z-10 text-center mb-6">
                  <div
                    className={`inline-block p-4 rounded-full bg-[#741112]/10 transition-all duration-500 ${
                      hoveredCard === idx ? "scale-110 rotate-12" : ""
                    }`}
                  >
                    {item.icon}
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-[#741112] mb-4 text-center">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-center mb-6">{item.description}</p>
                <div className="space-y-2 mb-6">
                  {item.highlights.map((highlight, i) => (
                    <div key={i} className="flex items-center text-sm text-gray-500">
                      <CheckCircle className="w-4 h-4 text-[#741112] mr-2" />
                      {highlight}
                    </div>
                  ))}
                </div>
                <div className="text-center">
                  <span className="inline-flex items-center px-4 py-2 bg-[#741112] text-white rounded-full text-sm font-semibold shadow-lg">
                    {item.stats}
                    <TrendingUp className="w-4 h-4 ml-2" />
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Marketing Solutions */}
        <section ref={solutionsRef} className="bg-gray-50 py-24 px-4 relative">
  <div className="text-center mb-20">
    <div className="inline-block relative mb-6">
      <div className="bg-[#741112] p-4 rounded-full shadow-xl">
        <TargetIcon className="w-8 h-8 text-white" />
      </div>
    </div>
    <h2 className="text-5xl md:text-6xl font-bold text-[#741112] mb-6">
      Project Marketing Solutions
    </h2>
    <div className="w-40 h-1 bg-[#741112] mx-auto rounded-full mb-4" />
    <p className="text-gray-600 text-lg max-w-3xl mx-auto">
      Strategic solutions that transform your projects into market leaders
    </p>
  </div>

  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-7xl mx-auto">
    {marketingSolutions.map((item, i) => (
      <div
        key={i}
        className="group bg-white p-8 rounded-2xl border border-gray-100 shadow-lg hover:shadow-2xl transition-all duration-500 flex items-start gap-6 relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-[#741112]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        <div className="relative z-10 flex-shrink-0">
          <div className="p-4 rounded-xl bg-[#741112]/10 transform transition-all duration-500 group-hover:scale-110 group-hover:rotate-6 shadow-md">
            {item.icon}
          </div>
        </div>
        <div className="relative z-10 flex-1">
          <h3 className="text-xl font-bold text-[#741112] mb-3">
            {item.text}
          </h3>
          <p className="text-gray-600 mb-4">{item.description}</p>
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-green-600 bg-green-50 px-3 py-1 rounded-full">
              {item.benefit}
            </span>

            {/* ✅ Click to Call */}
            <a
              href="tel: +91 22 4660 4783"  // <-- replace with your number
              className="flex items-center text-[#741112] opacity-0 group-hover:opacity-100 transform translate-x-4 group-hover:translate-x-0 transition-all duration-500"
            >
              <span className="text-sm font-semibold mr-2">Know More</span>
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    ))}
  </div>
</section>


        {/* CTA */}
        <section className="bg-[#741112] py-24 px-4 text-white text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-6xl font-bold mb-6">
              Ready to Transform Your Real Estate Sales?
            </h2>
            <p className="text-xl md:text-2xl mb-10 text-white/90">
              Join thousands of successful real estate developers who trust our expertise
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <button
                onClick={() => setShowPopup(true)} // ✅ open popup
                className="bg-white text-[#741112] px-8 py-4 rounded-full font-bold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-2xl"
              >
                Get Started Today
              </button>
            </div>
          </div>
        </section>
      </main>

      {/* ✅ PopupForm rendering */}
      {showPopup && <PopupForm onClose={() => setShowPopup(false)} />}

      <Footer />
    </>
  );
};

export default ServicesPage;
